import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { useGetSmartLinksQuery } from '../../../../redux/api/fymAPI';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { formMessages } from './formMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for every block form */
import styles from './SMLBlockForm.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface LinkPageListProps {
  accountId: number;
  /** The page of the editor: a link to itself is never offered. */
  smartLinkId: number;
  selectedId: number | null;
  onPick: (smartLinkId: number) => void;
}

interface PageRowProps {
  id: number;
  name: string;
  slug: string;
  isDraft: boolean;
  isOn: boolean;
  onPick: (smartLinkId: number) => void;
}

const PageRowComponent: React.FC<PageRowProps> = ({ id, name, slug, isDraft, isOn, onPick }) => {
  const intl = useIntl();
  const pick = useCallback(() => onPick(id), [id, onPick]);
  return (
    <button type="button" className={clsx(styles.pickRow, isOn && styles.pickRowOn)} onClick={pick} aria-pressed={isOn}>
      <span className={styles.optionText}>
        {name}
        <span className={styles.optionSub}>{isDraft ? intl.formatMessage(formMessages.pageDraft) : `/${slug}`}</span>
      </span>
    </button>
  );
};

const PageRow = TypedMemo(PageRowComponent);

/** "One of my pages" (design d3): the account's other pages, published first. */
const LinkPageListComponent: React.FC<LinkPageListProps> = ({ accountId, smartLinkId, selectedId, onPick }) => {
  const intl = useIntl();
  const { data, isLoading, error, refetch } = useGetSmartLinksQuery({ accountId, page: 0, limit: 50 });
  const pages = useMemo(
    () =>
      (data?.results ?? [])
        .filter((page) => page.id !== smartLinkId)
        .map((page) => ({
          id: page.id,
          name: (page.publishedSmartLinkVersion ?? page.recentSmartLinkVersion).name,
          slug: (page.publishedSmartLinkVersion ?? page.recentSmartLinkVersion).slug,
          isDraft: page.publishedSmartLinkVersion === null,
        }))
        .sort((a, b) => Number(a.isDraft) - Number(b.isDraft)),
    [data, smartLinkId]
  );
  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  if (isLoading) {
    return <LoadingView />;
  }
  if (error !== undefined) {
    return <ErrorView error={error} retry={retry} />;
  }
  if (pages.length === 0) {
    return <p className={styles.help}>{intl.formatMessage(formMessages.noOtherPages)}</p>;
  }
  return (
    <div className={styles.optionList} data-testid="link-page-list">
      {pages.map((page) => (
        <PageRow
          key={page.id}
          id={page.id}
          name={page.name}
          slug={page.slug}
          isDraft={page.isDraft}
          isOn={page.id === selectedId}
          onPick={onPick}
        />
      ))}
    </div>
  );
};

export const LinkPageList = TypedMemo(LinkPageListComponent);
